import { useId, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { cn } from "@ridereserv/shared/utils";
import { Button } from "@/components/ui/Button";
import { Logo } from "@/components/ui/Logo";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { useAuth, useAuthActions } from "@/hooks/useAuth";

/**
 * The public nav targets, in header/footer nav order. These point at the
 * homepage in-page anchors (the standalone /features, /about, /contact pages
 * were retired in favor of homepage sections). On the homepage they smooth-
 * scroll; from any other page react-router navigates home and the home route's
 * hash-scroll effect jumps to the section. "Contact" resolves to the footer.
 */
const marketingLinks = [
  { to: "/#features", label: "Features" },
  { to: "/#about", label: "About" },
  { to: "/#contact", label: "Contact" },
] as const;

/**
 * Shared, auth-aware site header for every public surface (homepage, legal).
 * Consolidates the previously-divergent headers into one:
 *
 * - Logo (links home) on the left.
 * - Inline nav (Features / About / Contact) pointing at the homepage in-page
 *   anchors; hidden below `md`, exposed in the mobile drawer instead.
 * - An auth-aware account area: a skeleton while the session loads, a
 *   Dashboard/My-account + Sign-Out pair when signed in, or Sign-In +
 *   Get-Started when signed out. The "Dashboard" vs "My account" label and the
 *   "Sign Out" / "Sign In" / "Get Started" copy are part of the E2E contract —
 *   do not reword them.
 * - A {@link ThemeToggle}, visible at every breakpoint.
 * - An accessible hamburger disclosure that opens a hairline-separated drawer
 *   carrying the marketing nav + auth actions on small screens. The open drawer
 *   is the only place a shadow is permitted (it is an overlay).
 *
 * Sticky, hairline-bordered, `bg-surface/95 backdrop-blur` — flip-safe across
 * light and dark themes, token classes only.
 * @returns The site header element.
 */
export function SiteHeader() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, memberships } = useAuth();
  const { logout } = useAuthActions();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const drawerId = useId();

  // Close the drawer after any action inside it so a tapped link/button never
  // leaves the overlay hanging open over the next view. Called from the drawer's
  // own handlers rather than a route-change effect (which causes cascading
  // renders) — the drawer only exists on small screens, so this is cheap.
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Close the drawer, then navigate. `navigate` returns a Promise in
  // react-router; it is fire-and-forget here, hence `void`.
  const closeAndGo = (path: string) => {
    closeMenu();
    void navigate(path);
  };

  // Match the account label to where `/dashboard` actually routes the user:
  // company members (admin/manager) land on a company dashboard ("Dashboard"),
  // everyone else lands on their personal account area ("My account"). The
  // destination is identical — only the label branches.
  const isCompanyMember = memberships.some((m) =>
    m.roles.some((role) => role === "admin" || role === "manager")
  );
  const accountLabel = isCompanyMember ? "Dashboard" : "My account";

  const handleSignOut = async () => {
    await logout();
    void navigate("/");
  };

  return (
    <header className="sticky top-0 z-30 border-b border-neutral-200 bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between gap-4">
          {/* Brand + inline nav */}
          <div className="flex items-center gap-6">
            <Logo asLink />
            <nav
              aria-label="Primary"
              className="hidden items-center gap-0.5 md:flex"
            >
              {marketingLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={cn(
                    "inline-flex min-h-11 items-center rounded-sm px-3 text-sm font-medium transition-colors pointer-fine:min-h-8",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                    "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Account controls + theme toggle */}
          <div className="flex items-center gap-1 sm:gap-2">
            <ThemeToggle />

            {/* Auth area: the primary control stays in the bar at every
                breakpoint so logged-in/out state is always visible; the
                secondary control is demoted to the mobile drawer below. */}
            <nav aria-label="Account" className="flex items-center gap-2">
              {isLoading ? (
                <div
                  aria-hidden="true"
                  className="h-9 w-24 animate-pulse rounded-sm bg-neutral-100 sm:w-40"
                />
              ) : isAuthenticated ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/dashboard")}
                  >
                    {accountLabel}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSignOut}
                    className="hidden md:inline-flex"
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/login")}
                    className="hidden md:inline-flex"
                  >
                    Sign In
                  </Button>
                  <Button size="sm" onClick={() => navigate("/register")}>
                    Get Started
                  </Button>
                </>
              )}
            </nav>

            {/* Mobile disclosure trigger */}
            <button
              type="button"
              aria-expanded={isMenuOpen}
              aria-controls={drawerId}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              onClick={() => {
                setIsMenuOpen((open) => !open);
              }}
              className={cn(
                "inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm md:hidden",
                "text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900",
                "pointer-fine:min-h-9 pointer-fine:min-w-9",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              )}
            >
              {isMenuOpen ? (
                <X className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
              ) : (
                <Menu className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer — the only place a shadow is allowed (it is an overlay). */}
      {isMenuOpen && (
        <div
          id={drawerId}
          className="border-t border-neutral-200 bg-surface shadow-lg md:hidden"
        >
          <nav
            aria-label="Primary"
            className="mx-auto max-w-7xl space-y-1 px-4 py-3 sm:px-6 lg:px-8"
          >
            {marketingLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={closeMenu}
                className={cn(
                  "flex min-h-11 items-center rounded-sm px-3 text-sm font-medium transition-colors",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                  "text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900"
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="mx-auto max-w-7xl border-t border-neutral-200 px-4 py-3 sm:px-6 lg:px-8">
            {isLoading ? (
              <div
                aria-hidden="true"
                className="h-11 w-full animate-pulse rounded-sm bg-neutral-100"
              />
            ) : isAuthenticated ? (
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="md"
                  className="w-full"
                  onClick={() => {
                    closeAndGo("/dashboard");
                  }}
                >
                  {accountLabel}
                </Button>
                <Button
                  variant="ghost"
                  size="md"
                  className="w-full"
                  onClick={() => {
                    closeMenu();
                    void handleSignOut();
                  }}
                >
                  Sign Out
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="md"
                  className="w-full"
                  onClick={() => {
                    closeAndGo("/login");
                  }}
                >
                  Sign In
                </Button>
                <Button
                  size="md"
                  className="w-full"
                  onClick={() => {
                    closeAndGo("/register");
                  }}
                >
                  Get Started
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
